import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { defaultEventCategorySet, TaskItem } from '@demind-inc/core';
import { snakeCase } from 'lodash';

import { ChatSuggestedEvent } from '../types';
import { TodoAdjustment } from '../api/science/openapi';

dayjs.extend(utc);
dayjs.extend(timezone);

/** OpenAPI TodoAdjustment on web may lag science; ai_note is present at runtime. */
type TodoAdjustmentInput = TodoAdjustment & {
  ai_note?: string | null;
};

export const TEMPORAL_TASK_PREFIX = 'temporal-';

const DEFAULT_TASK_DURATION_MIN = 30;

/** Real, existing todo items ('update'/'remove') always carry `todo_id` per
 * the API contract; a brand-new suggestion ('add') doesn't have one yet (the
 * backend assigns it on commit) — fall back to `name`, the same tradeoff
 * already accepted for meals/sleep (no UUID available, small collision risk
 * across two same-named pending suggestions, accepted as a known limit). */
const resolveTaskEventId = (task: TodoAdjustmentInput): string =>
  task.todo_id ? task.todo_id : `${TEMPORAL_TASK_PREFIX}task-${task.name}`;

const isRemovedTask = (task: TodoAdjustmentInput): boolean =>
  task.action === 'remove' || task.deleted === true;

/** Same clock check as lifestack_backend's transformAdjustmentCTA. */
const isValidHHmm = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.toLowerCase() !== 'null' &&
  /^\d{1,2}:\d{2}$/.test(value.trim());

/** earliest_start / latest_finish stay on `date`. Mirrors backend
 * transformHHmmToDate — a todo window is not a sleep range, so 00:00 is
 * midnight on that date, not the next day. */
const transformHHmmToTargetDateISO = (time: string, date: string): string => {
  const [hour, minute] = time.split(':').map((value) => parseInt(value, 10));
  const dateStr = `${date}T${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}:00`;
  return dayjs.tz(dateStr, dayjs.tz.guess()).toISOString();
};

/** Same category lookup as routine adjustments / transformAdjustmentCTA.
 * `light_task` resolves to Light Task (`default_lightTask`). */
const resolveCategoryId = (category: string | null | undefined): string | undefined => {
  if (!category) {
    return undefined;
  }
  const normalized = snakeCase(category);
  return (
    defaultEventCategorySet.find(
      (c) =>
        c.id === category ||
        c.name.toLowerCase() === category.toLowerCase() ||
        snakeCase(c.name) === normalized
    )?.id ?? category
  );
};

const resolveTargetDate = (task: TaskItem | undefined, date: string): string => {
  if (task?.dueDateTime?.date) {
    return task.dueDateTime.date;
  }
  if (task?.dueDateTime?.datetime) {
    return dayjs(task.dueDateTime.datetime).format('YYYY-MM-DD');
  }
  return date;
};

/**
 * Maps a v4 result_json's raw `adjustments.todo_list` (TodoAdjustment[]) into
 * ChatSuggestedEvent[], matching lifestack_calendar's
 * transformResultJsonTasksToSuggestedEvents and lifestack_backend's
 * transformAdjustmentCTA task branch.
 *
 * A todo does not need a concrete slot. `earliest_start` / `latest_finish`
 * are an optional window (rendered when present). `suggested_start_time` /
 * `suggested_end_time` are not used. `action: 'remove'` and `deleted` become
 * a deleteTask card; the caller also evicts any earlier card for that id.
 */
export const transformResultJsonTasksToSuggestedEvents = (
  tasks: TodoAdjustmentInput[],
  date: string,
  originalTasks: TaskItem[] = []
): ChatSuggestedEvent[] => {
  return tasks.flatMap((task): ChatSuggestedEvent[] => {
    if (!task.name && !isRemovedTask(task)) {
      return [];
    }

    const beforeTask = task.todo_id
      ? originalTasks.find((item) => item.taskId === task.todo_id)
      : undefined;
    const targetDate = resolveTargetDate(beforeTask, date);
    const eventId = resolveTaskEventId(task);
    const startTime = isValidHHmm(task.earliest_start)
      ? transformHHmmToTargetDateISO(task.earliest_start, targetDate)
      : '';
    const endTime = isValidHHmm(task.latest_finish)
      ? transformHHmmToTargetDateISO(task.latest_finish, targetDate)
      : '';
    const beforeStart = beforeTask?.startDateTime?.datetime
      ? new Date(beforeTask.startDateTime.datetime).toISOString()
      : undefined;
    const beforeEnd = beforeTask?.dueDateTime?.datetime
      ? new Date(beforeTask.dueDateTime.datetime).toISOString()
      : undefined;

    if (isRemovedTask(task)) {
      if (beforeTask?.deleted) {
        return [];
      }
      return [
        {
          eventId,
          name: beforeTask?.name || task.name || 'Task',
          date: targetDate,
          startTime: beforeStart || startTime,
          endTime: beforeEnd || endTime,
          durationMin:
            (beforeTask?.duration?.unit === 'minute' ? beforeTask.duration.amount : undefined) ||
            task.duration ||
            DEFAULT_TASK_DURATION_MIN,
          before: {
            startTime: beforeStart,
            endTime: beforeEnd,
            name: beforeTask?.name,
            date: targetDate,
          },
          ...(beforeTask && {
            taskFrom: {
              taskId: beforeTask.taskId,
              boardId: beforeTask.boardId!,
              from: beforeTask.appFrom!,
            },
          }),
          autoScheduleFrom: {
            from: 'todo',
            id: beforeTask?.taskId || task.todo_id || eventId,
            predicted: true,
          },
          isNew: false,
          action: 'deleteTask',
        },
      ];
    }

    return [
      {
        eventId,
        name: task.name,
        date: targetDate,
        startTime,
        endTime,
        description: task.explanation ?? task.ai_note ?? undefined,
        categoryId: resolveCategoryId(task.category),
        durationMin: task.duration || DEFAULT_TASK_DURATION_MIN,
        ...(beforeTask && {
          before: {
            startTime: beforeStart,
            endTime: beforeEnd,
            name: beforeTask.name,
            date: targetDate,
          },
          taskFrom: {
            taskId: beforeTask.taskId,
            boardId: beforeTask.boardId!,
            from: beforeTask.appFrom!,
          },
        }),
        autoScheduleFrom: { from: 'todo', id: task.todo_id || eventId, predicted: true },
        isNew: !beforeTask,
        action: 'setTask',
      },
    ];
  });
};

/** eventIds of tasks this turn marked `action: 'remove'` or `deleted`. The
 * caller drops any earlier chat card for that id; the delete itself is also
 * rendered as a deleteTask block by transformResultJsonTasksToSuggestedEvents. */
export const getRemovedTaskEventIds = (tasks: TodoAdjustmentInput[]): string[] =>
  tasks.filter(isRemovedTask).map((task) => resolveTaskEventId(task));
